import { watchImmediate } from '@vueuse/core';
import { AxiosError } from 'axios';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { storeToRefs } from 'pinia';
import { catchError, map, of, tap, throwError } from 'rxjs';
import { useRouter } from 'vue-router';

import { useSubscriptor } from '@silae/composables/subscriptor';
import { ROUTE_KEYS } from '~/router';
import { useBackendHttpService } from '~/services/http';
import { useAuthStore, useAuthorizationStore, usePrimordialSoupStore } from '~/stores';

const backendURL = import.meta.env.CE_BACKEND_BASE_URL;

function initAppPrimordialSoup() {
	/** fetch relevant data to initialize correctly Console mySilae Expense App */
	const { trigger } = useSubscriptor();
	const { isAuthenticated, isAdmin } = storeToRefs(useAuthorizationStore());
	const { fetchTemplates, fetchAgoInfo } = usePrimordialSoupStore();
	watchImmediate(
		() => ({ isAuthenticated: isAuthenticated.value, isAdmin: isAdmin.value }),
		(newData, oldData) => {
			const { isAuthenticated: newIsAuthenticated, isAdmin: newIsAdmin } = newData ?? {};
			const { isAuthenticated: oldIsAuthenticated, isAdmin: oldIsAdmin } = oldData ?? {};
			if (newIsAuthenticated && !oldIsAuthenticated) {
				trigger(fetchAgoInfo());

				if (newIsAdmin && newIsAdmin !== oldIsAdmin) {
					trigger(fetchTemplates());
				}
			}
		}
	);
}

export function initApp(): void {
	const { setBackendURL, setOnError } = useBackendHttpService();
	setBackendURL(backendURL);
	const authStore = useAuthStore();
	const { idToken, authSession } = storeToRefs(authStore);
	const { fetchAuthSession, validateTokenExpiration, setImpersonation, signOut } = authStore;
	const router = useRouter();

	// Check impersonation from stored token, in order to potentially reinit auth session
	if (idToken.value) {
		const decodedToken: JwtPayload & { impersonated: boolean } = jwtDecode(idToken.value);
		const { impersonated, exp } = decodedToken;
		if (impersonated && exp && exp * 1000 > Date.now()) {
			setImpersonation(true, idToken.value);
		}
	}

	const onError = (err: AxiosError) => {
		//TODO: switch case with common error status
		// We should probably plug it to a notification service
		// allowing us to show these errors as toasts with a relevant translated message.
		if (err?.response?.status === 401) {
			const authSession$ =
				authSession.value && validateTokenExpiration(authSession.value) ? of(authSession.value) : fetchAuthSession();
			return authSession$.pipe(
				map(session => {
					/**
					 * When failing to refresh auth session, no error is thrown, but we get an obj with attrs undefined:
					 * { tokens: undefined, credentials: undefined, identityId: undefined, userSub: undefined }
					 * hence we manually throw an error when needed and redirect to sign in page
					 */
					if (validateTokenExpiration(session)) {
						return session;
					} else {
						throw new Error('failed to refresh token');
					}
				}),
				catchError(() => {
					if (!!authSession.value) {
						signOut().pipe(tap(() => router.push({ name: ROUTE_KEYS.AUTH_SIGN_IN })));
					} else {
						return router.push({ name: ROUTE_KEYS.AUTH_SIGN_IN });
					}
				})
			);
		}
		return throwError(() => {
			return err;
		});
	};
	setOnError(onError);

	/** fetch primordial soup for app context initialization */
	initAppPrimordialSoup();
}
